import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Bell, ChevronDown, User, Settings, Moon, Sun, LogOut, MessageCircle, Menu, X, Home, FileText, Building, Phone, CheckCircle, CheckSquare, CreditCard, DollarSign, Calendar, Bot } from "lucide-react";
import pathIcon from "@/assets/path-icon.png";
import { LoanOfficerDialog } from "@/components/LoanOfficerDialog";
import { LoanSwitcher } from "@/components/LoanSwitcher";
import { NotificationsPopover } from "@/components/NotificationsPopover";
import { useLoanContacts } from "@/hooks/useLoanQuery";
import { useTasks } from "@/hooks/useTasks";
import { useState } from "react";
import { useTheme } from "@/hooks/useTheme";
import { useLoan } from "@/context/LoanContext";
import { useEConsentGateStatus } from "@/hooks/useEConsentGateStatus";
import { useAuth } from "@/contexts/AuthContext";
import { useBorrowerProfile } from "@/hooks/useBorrowerProfile";
import { stageOf, stateLabel, TabKey } from "@/lib/lifecycle";
import { useVisibleTabs } from "@/hooks/useVisibleTabs";
import { isNavPathActive } from "@/lib/navActive";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

const tabMeta: Record<TabKey, { label: string; mobileLabel: string; to: string; icon: any }> = {
  dashboard:   { label: "Dashboard",           mobileLabel: "Dashboard",   to: "/",         icon: Home },
  application: { label: "Application",         mobileLabel: "Application", to: "/application", icon: FileText },
  myloan:      { label: "My Loan",             mobileLabel: "My Loan",     to: "/my-loan",    icon: Building },
  myhome:      { label: "My Home",             mobileLabel: "My Home",     to: "/my-home",    icon: Home },
  documents:   { label: "Documents",           mobileLabel: "Documents",   to: "/documents",  icon: FileText },
  conditions:  { label: "Conditions",          mobileLabel: "Conditions",  to: "/conditions", icon: CheckCircle },
  payments:    { label: "Payments",            mobileLabel: "Payments",    to: "/payments",   icon: CreditCard },
  income:      { label: "Income Verification", mobileLabel: "Income",      to: "/income",     icon: DollarSign },
  contacts:    { label: "Contacts",            mobileLabel: "Contacts",    to: "/contacts",   icon: Phone },
  tasks:       { label: "Tasks",               mobileLabel: "Tasks",       to: "/tasks",      icon: CheckSquare },
};

/** Old Tasks feature showed a red dot on the nav tab while any task was Open — same signal here. */
function hasOpenTasks(tasks: ReturnType<typeof useTasks>['tasks']): boolean {
  if (!tasks) return false;
  return tasks.borrowerOpenTasks.length > 0 || tasks.coBorrowerOpenTasks.length > 0;
}

/** Persistent LO card in header — matches Lovable's quick-access LO pattern */
function HeaderLoanOfficerCard({ loanId }: { loanId: string | undefined }) {
  const { data: contactsData } = useLoanContacts(loanId);
  const lo = contactsData?.contacts?.find(
    (c) => c.role === 'Loan Officer' || c.role === 'loan_officer'
  );

  if (!lo) return null;

  const initials = lo.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
  const firstName = lo.name.split(' ')[0];

  return (
    <LoanOfficerDialog>
      <button
        className="hidden md:flex items-center gap-2 shrink-0 px-1 py-1 hover:opacity-80 transition-opacity min-h-[44px] touch-manipulation"
        data-testid="header-lo-card"
        aria-label={`Contact ${lo.name}, Loan Officer`}
      >
        {lo.photoUrl ? (
          <img src={lo.photoUrl} alt={lo.name} className="h-8 w-8 rounded-full object-cover" />
        ) : (
          <div className="h-8 w-8 rounded-full bg-info/15 text-info grid place-items-center text-xs font-semibold">
            {initials}
          </div>
        )}
        <div className="text-left hidden lg:block">
          <div className="text-xs font-semibold leading-tight">{firstName}</div>
          <div className="text-[10px] text-muted-foreground leading-tight">Loan Officer</div>
        </div>
      </button>
    </LoanOfficerDialog>
  );
}

export const Header = () => {
  const { state, loanId } = useLoan();
  // E-consent hard gate (ticket pending, 2026-07-23): an unconsented
  // borrower sees NO navigation at all — only the /e-consent page content.
  const { consented } = useEConsentGateStatus();
  // NP-408: flag-aware — Income Verification/Conditions hidden for v1.0.
  const tabs = useVisibleTabs(state);
  const { tasks: tasksBadgeData } = useTasks(tabs.includes('tasks') ? loanId : undefined);
  const showTasksBadge = hasOpenTasks(tasksBadgeData);
  const loc = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  // NP-490 — the avatar cannot come from `user`: that is hydrated from localStorage at
  // login, and the API deliberately leaves it off auth responses so token exchanges
  // never carry image bytes. Shared query with Settings, so uploading there updates
  // this icon in the same beat — the half of the bug QA reported.
  const { avatarUrl } = useBorrowerProfile();

  // Build user initials and display name from the logged-in user
  const userInitials = user
    ? `${(user.firstName?.[0] ?? '').toUpperCase()}${(user.lastName?.[0] ?? '').toUpperCase()}` || user.email?.[0]?.toUpperCase() || '?'
    : '?';
  const userDisplayName = user
    ? [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || 'User'
    : 'User';
  const userEmail = user?.email || '';
  // NP-70: the visibility gate moved inside LoanSwitcher (it filters by
  // isSwitcherVisibleStatus itself and renders the "Start an Application"
  // CTA when nothing is visible), so the Header always mounts it.
  // NP-45: theme is managed by useTheme (localStorage + account-record
  // persistence + system default), replacing the previous in-session-only state.
  const { isDark, toggle: toggleTheme } = useTheme();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  if (!consented) return null;

  return (
    <header className="relative md:sticky md:top-0 z-40 bg-card backdrop-blur-md border-b-2 border-border shadow-sm dark:bg-[hsl(218_24%_14%)] md:bg-card/95">
      {/* NP-500: flex-nowrap keeps the whole header on ONE row, per Suha's mobile
          design — nav collapses to the hamburger, avatar stays on the same row.
          It needs the opt-out added to the <=375px rule in index.css, which forces
          `flex-wrap: wrap !important` on every non-column flex container that does
          NOT carry `flex-nowrap`, and outranks Tailwind on specificity even so.
          Without both halves the group breaks
          onto a second line at 360px (it wants 260px; 256px is free). */}
      <div className="px-2 sm:px-6 py-2 sm:py-3.5 flex flex-nowrap items-center gap-1 sm:gap-2 min-w-0">
        {/* Brand — clickable, navigates home */}
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 shrink-0 cursor-pointer hover:opacity-80 transition-opacity min-h-[44px] min-w-[44px] touch-manipulation"
          aria-label="Go to home page"
          data-testid="header-logo-button"
        >
          <div className="h-11 w-11 rounded-xl bg-card border border-border/60 grid place-items-center shadow-sm overflow-hidden">
            <img src={pathIcon} alt="Path" className="h-7 w-7 object-contain" />
          </div>
          <span className="hidden sm:inline text-xl font-semibold tracking-tight">Path</span>
        </button>

        {/* Hamburger menu for mobile */}
        <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
          <SheetTrigger asChild>
            <button 
              className="lg:hidden flex items-center justify-center min-h-[44px] min-w-[44px] p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors touch-manipulation"
              data-testid="header-hamburger-menu-button"
            >
              <Menu className="size-5" />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[280px] flex flex-col" data-testid="header-mobile-menu-sheet">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2 text-left">
                <img src={pathIcon} alt="Path" className="h-7 w-7 object-contain" />
                Path
              </SheetTitle>
            </SheetHeader>
            <nav className="mt-6 space-y-1" data-testid="mobile-navigation">
              {tabs.map((k) => {
                const meta = tabMeta[k];
                const Icon = meta.icon;
                const active = isNavPathActive(meta.to, loc.pathname);
                return (
                  <button
                    key={k}
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate(meta.to);
                    }}
                    className={cn(
                      "flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-colors w-full text-left",
                      active
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                    )}
                    data-testid={`mobile-nav-${k}`}
                  >
                    <Icon className="size-5" />
                    {meta.label}
                    {k === 'tasks' && showTasksBadge && (
                      <span className="ml-auto size-2 rounded-full bg-destructive" data-testid="mobile-nav-tasks-badge" />
                    )}
                  </button>
                );
              })}

              {/* Sophia entry point */}
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  navigate('/sophia');
                }}
                className={cn(
                  "flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-colors w-full text-left",
                  loc.pathname === '/sophia'
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                )}
                data-testid="mobile-nav-sophia"
              >
                <Bot className="size-5" />
                Sophia AI
              </button>
            </nav>

            {/* User section at bottom of mobile menu */}
            <div className="mt-auto pt-4 border-t border-border/60">
              <div className="px-3 py-2">
                <div className="text-sm font-semibold">{userDisplayName}</div>
                <div className="text-xs text-muted-foreground">{userEmail}</div>
              </div>
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  navigate('/settings');
                }}
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted w-full text-left transition-colors"
                data-testid="mobile-nav-settings"
              >
                <Settings className="size-4" />
                Settings
              </button>
              <button
                onClick={async () => {
                  setIsMobileMenuOpen(false);
                  await logout();
                  navigate('/login', { replace: true });
                }}
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-destructive hover:bg-muted w-full text-left transition-colors"
                data-testid="mobile-nav-logout"
              >
                <LogOut className="size-4" />
                Log out
              </button>
            </div>
          </SheetContent>
        </Sheet>

        {/* Pill nav — left aligned next to logo, hidden on mobile.
            NP-800: shown from `lg`, not `md`. A phone in landscape (~844–932px) is
            past `md`, and the pills plus the switcher, LO card, bell and avatar don't
            fit there: the nav is the only shrinkable item, so its overflow-x-auto
            clipped it (scrollbar hidden) — "Documents" cut off at the switcher's edge
            and "Contacts" out of view. Below `lg` the hamburger carries every tab. */}
        <nav className="hidden lg:flex flex-1 justify-start min-w-0 items-center gap-1.5" data-testid="main-navigation">
          <div className="flex overflow-x-auto scrollbar-hide w-full">
            <div className="inline-flex gap-0.5 min-w-max rounded-full bg-muted p-1" data-testid="nav-tabs-container">
              {tabs.map((k) => {
                const meta = tabMeta[k];
                const active = isNavPathActive(meta.to, loc.pathname);
                return (
                  <NavLink
                    key={k}
                    to={meta.to}
                    className={cn(
                      "flex-none px-3 sm:px-4 py-1.5 min-h-[36px] min-w-[44px] touch-manipulation rounded-full transition-colors text-center",
                      active
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                    data-testid={`header-nav-${k}`}
                  >
                    <span className="text-sm font-medium whitespace-nowrap">
                      <span className="hidden sm:inline">{meta.label}</span>
                      <span className="sm:hidden">{meta.mobileLabel}</span>
                      {k === 'tasks' && showTasksBadge && (
                        <span className="ml-1.5 inline-block size-1.5 rounded-full bg-destructive align-middle" data-testid="header-nav-tasks-badge" />
                      )}
                    </span>
                  </NavLink>
                );
              })}
            </div>
          </div>
        </nav>

        {/* NP-500: the trailing controls are pinned right by THIS wrapper's ml-auto, not
            by the nav above.
            The nav is `hidden md:flex flex-1` — the only flex-grow item in the row — so
            below `md` it is display:none and stops being a flex item at all. Nothing was
            left to take up the free space, and the whole group bunched against the
            hamburger: on a 360px Android viewport the avatar ended at x=204 with 156px of
            dead space to its right, which is what QA reported as the avatar not being in
            the upper right.
            Grouping them also means the ml-auto lives in one place instead of on each
            control: the free space collects at the group's left edge and the controls
            stay flush with one another.
            The row USED to wrap at <=375px, so grouping matters for a second reason: a
            parallel fix here concluded "the row is the default flex-nowrap" from the
            class list, which is a reasonable read and was still wrong. Nothing in this
            file set it — src/index.css (~638) forces
            `.flex:not(.flex-col):not(.flex-nowrap) { flex-wrap: wrap !important }` under
            a max-width:375px media query, and this row now opts out via the
            `flex-nowrap` above. Measured before that opt-out existed: computed
            flex-wrap was `wrap` at 360px and `nowrap` at 400px, exactly that boundary.
            So on QA's 360px Galaxy S25 ungrouped controls would scatter across two
            lines; grouped, they move together. */}
        <div
          // NP-500: the wrapper needs flex-nowrap too. Fixing only the outer row left
          // this one wrapping its own children under the same <=375px rule — the
          // switcher landed on one line and the bell/avatar on the next, and
          // `items-center` on the outer row then centred the double-height group,
          // pushing the logo and hamburger down with it.
          className="ml-auto flex flex-nowrap items-center gap-1 sm:gap-2 min-w-0"
          data-testid="header-trailing-controls"
        >
          {/* Loan switcher — both mobile and desktop variants */}
          <LoanSwitcher />

          {/* LO card in header — Lovable pattern: persistent quick-access LO info.
              NP-113: not for a closed loan, whose borrower reaches the LO through the
              Contacts tab; the post-close dashboard carries no LO contact of its own. */}
          {stageOf(state) !== "Post-Close" && <HeaderLoanOfficerCard loanId={loanId} />}

          <NotificationsPopover />

          <DropdownMenu>
            {/* BUG-DEEP-011 fix: add aria-label and title so the avatar dropdown is
                discoverable — Sven found logout was hidden with no hint it’s a menu */}
            <DropdownMenuTrigger asChild>
              <button
                className="flex flex-nowrap items-center gap-1 min-h-[44px] min-w-[44px] pl-0 pr-1.5 rounded-full bg-transparent hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background touch-manipulation"
                data-testid="header-user-menu-button"
                aria-label="Account menu — profile, settings, sign out"
                title="Account menu"
              >
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    // Decorative: the button already carries an aria-label naming the
                    // account menu, so alt text here would just repeat it.
                    alt=""
                    className="h-11 w-11 rounded-full object-cover shadow-sm"
                    data-testid="header-user-avatar"
                  />
                ) : (
                  <span className="h-11 w-11 rounded-full bg-accent text-accent-foreground grid place-items-center text-xs font-semibold shadow-sm" data-testid="header-user-initials">{userInitials}</span>
                )}
                <ChevronDown className="size-3.5 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="font-semibold">{userDisplayName}</div>
                <div className="text-[11px] font-normal text-muted-foreground">{userEmail}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild className="focus:bg-muted focus:text-foreground hover:bg-muted hover:text-foreground dark:focus:bg-muted dark:hover:bg-muted">
                <NavLink to="/settings" data-testid="header-user-menu-profile"><User className="size-4 mr-2" /> Profile</NavLink>
              </DropdownMenuItem>
              <DropdownMenuItem asChild className="focus:bg-muted focus:text-foreground hover:bg-muted hover:text-foreground dark:focus:bg-muted dark:hover:bg-muted">
                <NavLink to="/settings" data-testid="header-user-menu-settings"><Settings className="size-4 mr-2" /> Settings</NavLink>
              </DropdownMenuItem>
              {/* NP-45: theme toggle is always available in the account menu. */}
              <DropdownMenuItem onSelect={(e) => { e.preventDefault(); toggleTheme(); }} className="focus:bg-muted focus:text-foreground hover:bg-muted hover:text-foreground dark:focus:bg-muted dark:hover:bg-muted" data-testid="header-user-menu-theme-toggle">
                {isDark ? <Sun className="size-4 mr-2" /> : <Moon className="size-4 mr-2" />}
                {isDark ? "Light mode" : "Dark mode"}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => {
                  // Fire-and-forget: let the menu close naturally, then log out.
                  // setTimeout(0) ensures the Radix menu teardown finishes
                  // before we clear auth state and navigate — prevents the
                  // "element detached" race hit in R16 E2E.
                  setTimeout(async () => {
                    await logout();
                    navigate("/login", { replace: true });
                  }, 0);
                }}
                className="text-destructive focus:text-destructive focus:bg-muted hover:bg-muted hover:text-destructive dark:focus:bg-muted dark:hover:bg-muted"
                data-testid="header-user-menu-logout"
              >
                <LogOut className="size-4 mr-2" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

    </header>
  );
};
